import { ProductAiAnalysisEntity } from '@/entities/product-ai-analysis.entity';
import { ProductAnalysisEntity } from '@/entities/product-analysis.entity';
import { useCallback } from 'react';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useDualGroupTableAnalysis } from '@/components/group-tables/use-group-table-analysis';

type ProductForAnalysis = {
    id: string;
    name: string;
};

type UseProductsTableAnalysisArgs = {
    accessToken: string;
};

export const useProductsTableAnalysis = <TProduct extends ProductForAnalysis>({
    accessToken,
}: UseProductsTableAnalysisArgs) => {
    const { productsApi } = useMarketplaceApi();

    const loadProductAnalysis = useCallback(
        async (product: TProduct, { signal }: { signal: AbortSignal }) => {
            const response = await productsApi.getProductAnalysis(product.id, {
                authToken: accessToken,
                signal,
            });
            return response.data;
        },
        [accessToken, productsApi],
    );

    const loadProductAiAnalysis = useCallback(
        async (product: TProduct, { signal }: { signal: AbortSignal }) => {
            const response = await productsApi.getProductAiAnalysis(product.id, {
                authToken: accessToken,
                signal,
            });
            return response.data;
        },
        [accessToken, productsApi],
    );

    const { primaryAnalysis, secondaryAnalysis } = useDualGroupTableAnalysis<
        TProduct,
        ProductAnalysisEntity,
        ProductAiAnalysisEntity
    >({
        primary: {
            load: loadProductAnalysis,
            errorMessage: 'Не удалось загрузить анализ продукта.',
        },
        secondary: {
            load: loadProductAiAnalysis,
            errorMessage: 'Не удалось загрузить AI-анализы группы продуктов.',
        },
    });

    return {
        openedProduct: primaryAnalysis.openedTarget,
        openedProductAnalysis: primaryAnalysis.openedTargetData,
        isProductAnalysisLoading: primaryAnalysis.isLoading,
        productAnalysisError: primaryAnalysis.error,
        onProductAnalysisClose: primaryAnalysis.onClose,
        onProductClick: primaryAnalysis.onOpen,
        openedProductAiResponse: secondaryAnalysis.openedTarget,
        openedProductAiData: secondaryAnalysis.openedTargetData,
        isProductAiLoading: secondaryAnalysis.isLoading,
        productAiError: secondaryAnalysis.error,
        onProductAiClose: secondaryAnalysis.onClose,
        onProductAiOpen: secondaryAnalysis.onOpen,
    };
};
